<template>
  <div class="quick-actions-fab" v-if="actions.length">
    <transition name="fab-expand">
      <div v-if="expanded" class="fab-menu">
        <button
            v-for="action in actions"
            :key="action.id"
            class="fab-action"
            :class="{ 'fab-action--disabled': action.disabled }"
            @click="handleAction(action)"
            :title="action.label"
        >
          <span class="fab-action-icon">
            <MdpsIcon :name="action.icon" size="small"/>
          </span>
          <span class="fab-action-label">{{ action.label }}</span>
        </button>
      </div>
    </transition>

    <button class="fab-trigger" @click="expanded = !expanded" :class="{ active: expanded }">
      <MdpsIcon v-if="!expanded" name="plus" size="medium"/>
      <MdpsIcon v-else name="x" size="medium"/>
    </button>
  </div>
</template>

<script setup lang="ts">
import {ref, computed} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useAuthStore} from '@/stores/authStore'
import MdpsIcon from '@mdps/icon-vue3'
import {showContributorUpgradeMessage} from '@/utils/roleMessages'

interface QuickAction {
  id: string
  label: string
  icon: string
  action: () => void
  disabled?: boolean
}

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const expanded = ref(false)

const actions = computed<QuickAction[]>(() => {
  const list: QuickAction[] = []
  const path = route.path

  if (path !== '/submit') {
    list.push({
      id: 'submit-initiative',
      label: 'Submit Initiative',
      icon: 'plus',
      disabled: !authStore.canCreateInitiative,
      action: () => {
        if (authStore.canCreateInitiative) router.push('/submit')
        else showContributorUpgradeMessage()
      },
    })
  }

  if (path !== '/search') {
    list.push({
      id: 'search',
      label: 'Search',
      icon: 'search',
      action: () => router.push('/search'),
    })
  }

  return list
})

function handleAction(action: QuickAction) {
  expanded.value = false
  action.action()
}
</script>

<style scoped>
.quick-actions-fab {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.fab-trigger {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #1e293b, #334155);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(30, 41, 59, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s;
}

.fab-trigger:hover {
  transform: scale(1.06);
  box-shadow: 0 6px 28px rgba(30, 41, 59, 0.4);
}

.fab-trigger.active {
  background: linear-gradient(135deg, #475569, #64748b);
  transform: rotate(0deg);
}

.fab-menu {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: flex-end;
}

.fab-action {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 1rem;
  border-radius: 999px;
  border: 1px solid #f1f5f9;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
  white-space: nowrap;
}

.fab-action:hover {
  transform: translateX(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

.fab-action--disabled {
  cursor: not-allowed;
  opacity: 0.6;
  filter: grayscale(0.35);
}

.fab-action--disabled:hover {
  transform: none;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.fab-action-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  flex-shrink: 0;
}

.fab-action-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1e293b;
}

.fab-expand-enter-active {
  transition: opacity 0.2s, transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.fab-expand-leave-active {
  transition: opacity 0.12s, transform 0.15s;
}

.fab-expand-enter-from {
  opacity: 0;
  transform: translateY(10px) scale(0.95);
}

.fab-expand-leave-to {
  opacity: 0;
  transform: translateY(5px) scale(0.98);
}

@media (max-width: 768px) {
  .quick-actions-fab {
    bottom: 1rem;
    right: 1rem;
  }

  .fab-trigger {
    width: 46px;
    height: 46px;
  }
}
</style>
